import { useState, useEffect, useMemo, useCallback } from 'react'
import api from '../../services/api'
import toast from 'react-hot-toast'
import { RefreshCw, Download, Search, Mail, Phone, MessageCircle, AlertTriangle, ClipboardList, X } from 'lucide-react'
import LoadingSpinner from '../LoadingSpinner'

/**
 * Payment Runway Tracker (pay-as-you-learn).
 * Shows every active enrolment ranked by how close the student's paid-for
 * access is to ending, so staff can follow up personally on top of the
 * automated reminder emails. Data: GET /finance/payment-runway (read-only).
 */

const STATUS_META = {
  access_ended: { label: 'Access ended', help: 'Used every paid-for lesson; balance still owed', tone: 'red' },
  overdue:      { label: 'Overdue', help: 'Invoice past its due date', tone: 'red' },
  running_low:  { label: 'Running low', help: 'Few paid-for lessons left', tone: 'amber' },
  due_soon:     { label: 'Due soon', help: 'Invoice due within 7 days', tone: 'amber' },
  no_payment:   { label: 'No payment', help: 'Enrolled but nothing paid yet', tone: 'slate' },
  stalled:      { label: 'Stalled', help: 'No payment for 30+ days', tone: 'slate' },
  on_track:     { label: 'On track', help: 'Healthy runway', tone: 'green' },
  paid_up:      { label: 'Paid up', help: 'No balance', tone: 'green' },
}
const ATTENTION = ['access_ended', 'overdue', 'running_low', 'due_soon']
const FILTERS = [
  { id: 'attention', label: 'Needs attention' },
  { id: 'access_ended', label: 'Access ended' },
  { id: 'overdue', label: 'Overdue' },
  { id: 'running_low', label: 'Running low' },
  { id: 'due_soon', label: 'Due soon' },
  { id: 'no_payment', label: 'No payment' },
  { id: 'stalled', label: 'Stalled' },
  { id: 'uncontacted', label: 'Not followed up' },
  { id: 'all', label: 'All' },
]
const PAGE = 25
const RECENT_FOLLOWUP_DAYS = 7

const OUTCOMES = [
  { id: 'contacted', label: 'Contacted' },
  { id: 'promised_to_pay', label: 'Promised to pay' },
  { id: 'payment_plan', label: 'Agreed payment plan' },
  { id: 'no_answer', label: 'No answer' },
  { id: 'dropped_out', label: 'Dropping out' },
  { id: 'other', label: 'Other' },
]
const outcomeLabel = (id) => OUTCOMES.find((o) => o.id === id)?.label || id

function hasRecentFollowup(it) {
  if (!it.last_followup) return false
  return (Date.now() - new Date(it.last_followup.at).getTime()) / 86400000 < RECENT_FOLLOWUP_DAYS
}

const TONES = {
  red:   'bg-red-500/10 text-red-500 border-red-500/30',
  amber: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
  slate: 'bg-slate-500/10 text-slate-400 border-slate-500/30',
  green: 'bg-green-500/10 text-green-500 border-green-500/30',
}

const ugx = (n) => `${Math.round(Number(n) || 0).toLocaleString()} UGX`
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—')

function dueLabel(days) {
  if (days == null) return null
  if (days < 0) return `${Math.abs(days)}d overdue`
  if (days === 0) return 'due today'
  return `in ${days}d`
}

function whatsappUrl(phone) {
  let digits = String(phone || '').replace(/\D/g, '')
  if (!digits) return null
  if (digits.startsWith('0')) digits = `256${digits.slice(1)}`
  return `https://wa.me/${digits}`
}

function csvEscape(v) {
  const s = v == null ? '' : String(v)
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

function FollowupModal({ item, dark, onClose, onSaved }) {
  const [outcome, setOutcome] = useState('contacted')
  const [note, setNote] = useState('')
  const [promised, setPromised] = useState('')
  const [saving, setSaving] = useState(false)
  const [history, setHistory] = useState(null)

  useEffect(() => {
    let alive = true
    api.get(`/finance/payment-runway/${item.enrollment_id}/followups`)
      .then((r) => alive && setHistory(r.data))
      .catch(() => alive && setHistory([]))
    return () => { alive = false }
  }, [item.enrollment_id])

  const submit = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      await api.post(`/finance/payment-runway/${item.enrollment_id}/followups`, {
        outcome, note, promised_date: outcome === 'promised_to_pay' ? promised : undefined,
      })
      toast.success('Follow-up saved')
      onSaved()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save follow-up')
    } finally {
      setSaving(false)
    }
  }

  const box = dark ? 'bg-[#141414] text-white border-white/10' : 'bg-white text-gray-900 border-gray-200'
  const field = dark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'
  const muted = dark ? 'text-gray-400' : 'text-gray-500'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose}>
      <div className={`w-full max-w-lg max-h-[90vh] overflow-y-auto border  p-5 ${box}`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="font-semibold">Log follow-up</h3>
            <p className={`text-xs ${muted}`}>{item.student_name} · {item.course_title}</p>
          </div>
          <button onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className={`block text-xs mb-1 ${muted}`}>Outcome</label>
            <select value={outcome} onChange={(e) => setOutcome(e.target.value)} className={`w-full px-3 py-2 text-sm border  ${field}`}>
              {OUTCOMES.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </div>
          {outcome === 'promised_to_pay' && (
            <div>
              <label className={`block text-xs mb-1 ${muted}`}>Promised payment date</label>
              <input type="date" required value={promised} onChange={(e) => setPromised(e.target.value)} className={`w-full px-3 py-2 text-sm border  ${field}`} />
            </div>
          )}
          <div>
            <label className={`block text-xs mb-1 ${muted}`}>Note {outcome === 'other' ? '(required)' : '(optional)'}</label>
            <textarea rows={3} maxLength={1000} required={outcome === 'other'} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="What was said, what was agreed…" className={`w-full px-3 py-2 text-sm border  ${field}`} />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className={`px-3 py-2 text-sm border  ${dark ? 'border-white/10' : 'border-gray-300'}`}>Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm  bg-blue-600 text-white disabled:opacity-60">{saving ? 'Saving…' : 'Save follow-up'}</button>
          </div>
        </form>

        <div className="mt-5">
          <p className={`text-xs uppercase tracking-wide mb-2 ${muted}`}>History</p>
          {history === null ? <p className={`text-sm ${muted}`}>Loading…</p>
            : history.length === 0 ? <p className={`text-sm ${muted}`}>No follow-ups logged yet.</p>
            : (
              <ul className="space-y-2">
                {history.map((h) => (
                  <li key={h.id} className={`text-sm border-l-2 pl-3 ${dark ? 'border-white/20' : 'border-gray-300'}`}>
                    <p><strong>{outcomeLabel(h.outcome)}</strong>{h.promised_date ? ` · promised ${fmtDate(h.promised_date)}` : ''}</p>
                    {h.note && <p className={muted}>{h.note}</p>}
                    <p className={`text-xs ${muted}`}>{fmtDate(h.created_at)}{h.by_name ? ` · ${h.by_name}` : ''}</p>
                  </li>
                ))}
              </ul>
            )}
        </div>
      </div>
    </div>
  )
}

export default function PaymentRunwayTracker({ dark = false }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState('attention')
  const [query, setQuery] = useState('')
  const [shown, setShown] = useState(PAGE)
  const [followupFor, setFollowupFor] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await api.get('/finance/payment-runway')
      setData(res.data)
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to load payment runway'
      setError(msg)
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])
  useEffect(() => { setShown(PAGE) }, [filter, query])

  const items = data?.items || []
  const summary = data?.summary

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return items.filter((it) => {
      if (filter === 'attention' && !ATTENTION.includes(it.status)) return false
      if (filter === 'uncontacted' && (!ATTENTION.includes(it.status) || hasRecentFollowup(it))) return false
      if (!['attention', 'all', 'uncontacted'].includes(filter) && !it.flags.includes(filter)) return false
      if (!q) return true
      return [it.student_name, it.student_number, it.email, it.phone, it.course_title]
        .some((v) => String(v || '').toLowerCase().includes(q))
    })
  }, [items, filter, query])

  const count = (id) => {
    if (id === 'all') return items.length
    if (id === 'attention') return items.filter((i) => ATTENTION.includes(i.status)).length
    if (id === 'uncontacted') return items.filter((i) => ATTENTION.includes(i.status) && !hasRecentFollowup(i)).length
    return items.filter((i) => i.flags.includes(id)).length
  }

  const exportCsv = () => {
    const head = ['Student', 'Student No', 'Email', 'Phone', 'Course', 'Status', 'Tuition', 'Paid', 'Balance',
      'Unlocked lessons', 'Completed', 'Remaining unlocked', 'Total lessons', 'Due date', 'Last payment', 'Last reminder', 'Last follow-up', 'Follow-up note', 'Promised date']
    const lines = filtered.map((i) => [
      i.student_name, i.student_number, i.email, i.phone, i.course_title,
      STATUS_META[i.status]?.label || i.status, i.tuition_amount, i.amount_paid, i.balance_due,
      i.unlocked_lessons, i.attended_unlocked_lessons, i.remaining_unlocked_lessons, i.total_lessons,
      i.due_date || '', i.last_payment_at ? i.last_payment_at.slice(0, 10) : '',
      i.last_reminder_at ? i.last_reminder_at.slice(0, 10) : '',
      i.last_followup ? `${outcomeLabel(i.last_followup.outcome)} (${i.last_followup.at.slice(0, 10)})` : '',
      i.last_followup?.note || '', i.last_followup?.promised_date || '',
    ].map(csvEscape).join(','))
    const blob = new Blob([[head.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `payment-runway-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const t = dark
    ? { text: 'text-white', muted: 'text-gray-400', card: 'bg-white/5 border-white/10', row: 'border-white/5 hover:bg-white/5', input: 'bg-white/5 border-white/10 text-white placeholder-gray-500', chip: 'border-white/10 text-gray-300 hover:bg-white/10', chipOn: 'bg-white/15 border-white/30 text-white', track: 'bg-white/10' }
    : { text: 'text-gray-900', muted: 'text-gray-500', card: 'bg-white border-gray-200', row: 'border-gray-100 hover:bg-gray-50', input: 'bg-white border-gray-300 text-gray-900 placeholder-gray-400', chip: 'border-gray-300 text-gray-600 hover:bg-gray-100', chipOn: 'bg-gray-900 border-gray-900 text-white', track: 'bg-gray-200' }

  if (loading && !data) return <LoadingSpinner />

  if (error && !data) {
    return (
      <div className={`border  p-6 text-center ${t.card}`}>
        <AlertTriangle className="mx-auto mb-2 text-amber-500" size={22} />
        <p className={`text-sm ${t.text}`}>{error}</p>
        <button onClick={load} className="mt-3 text-sm underline">Try again</button>
      </div>
    )
  }

  const tiles = [
    { id: 'attention', label: 'Needs attention', value: summary?.needs_attention ?? 0, tone: 'amber' },
    { id: 'access_ended', label: 'Access ended', value: summary?.by_status?.access_ended ?? 0, tone: 'red' },
    { id: 'overdue', label: 'Overdue', value: count('overdue'), tone: 'red' },
    { id: 'running_low', label: 'Running low', value: count('running_low'), tone: 'amber' },
  ]

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className={`text-lg font-semibold ${t.text}`}>Payment Runway</h2>
          <p className={`text-xs sm:text-sm mt-1 max-w-2xl ${t.muted}`}>
            Pay-as-you-learn: lessons unlock for whatever a student has paid. This shows how much paid-for
            learning each student has left, so you can follow up before their access runs out.
            Each attendance record marked present or late counts as one lesson used.
            Outstanding across tracked students: <strong>{ugx(summary?.outstanding_balance)}</strong>.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={loading} className={`inline-flex items-center gap-2 px-3 py-2 text-sm border  ${t.chip}`}>
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button onClick={exportCsv} disabled={!filtered.length} className={`inline-flex items-center gap-2 px-3 py-2 text-sm border  ${t.chip}`}>
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {tiles.map((tile) => (
          <button key={tile.id} onClick={() => setFilter(tile.id)} className={`text-left border  p-4 ${t.card}`}>
            <p className={`text-xs uppercase tracking-wide ${t.muted}`}>{tile.label}</p>
            <p className={`text-2xl font-bold mt-1 ${tile.value > 0 && tile.tone === 'red' ? 'text-red-500' : tile.value > 0 ? 'text-amber-500' : t.text}`}>{tile.value}</p>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 text-xs border  transition-colors ${filter === f.id ? t.chipOn : t.chip}`}>
            {f.label} <span className="opacity-70">({count(f.id)})</span>
          </button>
        ))}
        <div className="relative ml-auto w-full sm:w-64">
          <Search size={14} className={`absolute left-3 top-1/2 -translate-y-1/2 ${t.muted}`} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search student, course, phone…"
            className={`w-full pl-9 pr-3 py-2 text-sm border  ${t.input}`} />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className={`border  p-8 text-center text-sm ${t.card} ${t.muted}`}>
          {filter === 'attention' ? 'Nobody needs attention right now.' : 'No students match this view.'}
        </div>
      ) : (
        <div className={`border  overflow-x-auto ${t.card}`}>
          <table className="w-full text-sm min-w-[860px]">
            <thead>
              <tr className={`text-left text-xs uppercase tracking-wide ${t.muted}`}>
                <th className="px-4 py-3">Student</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Paid / balance</th>
                <th className="px-4 py-3">Paid-for lessons</th>
                <th className="px-4 py-3">Due</th>
                <th className="px-4 py-3">Last payment · reminder</th>
                <th className="px-4 py-3">Follow-up</th>
                <th className="px-4 py-3">Contact</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, shown).map((it) => {
                const meta = STATUS_META[it.status] || STATUS_META.on_track
                const pct = it.unlocked_lessons > 0 ? Math.min(100, (it.attended_unlocked_lessons / it.unlocked_lessons) * 100) : 0
                const wa = whatsappUrl(it.phone)
                const due = dueLabel(it.days_to_due)
                return (
                  <tr key={it.enrollment_id} className={`border-t align-top ${t.row}`}>
                    <td className="px-4 py-3">
                      <p className={`font-medium ${t.text}`}>{it.student_name || '—'}</p>
                      <p className={`text-xs ${t.muted}`}>{it.course_title}{it.student_number ? ` · ${it.student_number}` : ''}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span title={meta.help} className={`inline-block px-2 py-0.5 text-xs border  whitespace-nowrap ${TONES[meta.tone]}`}>{meta.label}</span>
                      {it.flags.length > 1 && (
                        <p className={`text-[11px] mt-1 ${t.muted}`}>+ {it.flags.filter((f) => f !== it.status).map((f) => STATUS_META[f]?.label || f).join(', ')}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <p className={t.text}>{ugx(it.amount_paid)} <span className={t.muted}>({it.percent_paid}%)</span></p>
                      <p className={`text-xs ${it.balance_due > 0 ? 'text-amber-500' : t.muted}`}>Balance {ugx(it.balance_due)}</p>
                    </td>
                    <td className="px-4 py-3 min-w-[170px]">
                      <p className={t.text}>
                        {it.remaining_unlocked_lessons} left
                        <span className={t.muted}> · {it.attended_unlocked_lessons}/{it.unlocked_lessons} attended · {it.unlocked_lessons}/{it.total_lessons} unlocked</span>
                      </p>
                      <div className={`h-1.5 mt-1.5  ${t.track}`}>
                        <div className={`h-1.5  ${it.remaining_unlocked_lessons === 0 && it.unlocked_lessons > 0 ? 'bg-red-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <p className={t.text}>{fmtDate(it.due_date)}</p>
                      {due && <p className={`text-xs ${it.days_to_due < 0 ? 'text-red-500' : t.muted}`}>{due}</p>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <p className={t.text}>{fmtDate(it.last_payment_at)}</p>
                      <p className={`text-xs ${t.muted}`}>
                        {it.last_reminder_at ? `Reminded ${fmtDate(it.last_reminder_at)}` : 'No reminder sent'}
                      </p>
                    </td>
                    <td className="px-4 py-3 min-w-[150px]">
                      {it.last_followup ? (
                        <>
                          <p className={t.text}>{outcomeLabel(it.last_followup.outcome)}</p>
                          {it.last_followup.promised_date && (
                            <p className={`text-xs ${new Date(it.last_followup.promised_date) < new Date(new Date().toDateString()) && it.balance_due > 0 ? 'text-red-500' : t.muted}`}>
                              Promised {fmtDate(it.last_followup.promised_date)}
                            </p>
                          )}
                          <p className={`text-xs ${t.muted}`}>{fmtDate(it.last_followup.at)}{it.last_followup.by ? ` · ${it.last_followup.by}` : ''}</p>
                          {it.last_followup.note && <p className={`text-xs mt-0.5 ${t.muted}`} title={it.last_followup.note}>{it.last_followup.note.length > 60 ? `${it.last_followup.note.slice(0, 60)}…` : it.last_followup.note}</p>}
                        </>
                      ) : (
                        <p className={`text-xs ${t.muted}`}>Not followed up</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        {it.email && <a href={`mailto:${it.email}`} title={it.email} className={t.muted}><Mail size={16} /></a>}
                        {it.phone && <a href={`tel:${it.phone}`} title={it.phone} className={t.muted}><Phone size={16} /></a>}
                        {wa && <a href={wa} target="_blank" rel="noreferrer" title="WhatsApp" className={t.muted}><MessageCircle size={16} /></a>}
                        <button onClick={() => setFollowupFor(it)} title="Log follow-up" className={t.muted}><ClipboardList size={16} /></button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {filtered.length > shown && (
            <div className="p-3 text-center">
              <button onClick={() => setShown((n) => n + PAGE)} className="text-sm underline">
                Show more ({filtered.length - shown} remaining)
              </button>
            </div>
          )}
        </div>
      )}
      {followupFor && (
        <FollowupModal
          item={followupFor}
          dark={dark}
          onClose={() => setFollowupFor(null)}
          onSaved={() => { setFollowupFor(null); load() }}
        />
      )}
    </div>
  )
}
